import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n, localizeApiUrl } from './i18n/I18nContext';

interface MsmSource {
  name: string;
  url: string;
}

interface StoryComment {
  id: number;
  content: string;
  username: string;
  created_at: string;
  replies: StoryComment[];
}

interface Story {
  id: number;
  slug: string | null;
  headline: string;
  summary: string;
  source_name: string;
  source_url: string;
  paper_title: string | null;
  paper_doi: string | null;
  article_id: number | null;
  article_slug: string | null;
  article_publisher: string | null;
  article_category: string | null;
  image_url: string | null;
  msm_sources: MsmSource[];
  topic: string;
  created_at: string;
  ai_service: string;
  comment_count: number;
}

const TOPIC_KEYS = ['', 'health', 'science', 'psychology', 'law', 'education', 'hotp'] as const;
const TOPIC_I18N: Record<string, string> = {
  '': 'story_topic_all',
  health: 'story_topic_health',
  science: 'story_topic_science',
  psychology: 'story_topic_psychology',
  law: 'story_topic_law',
  education: 'story_topic_education',
  hotp: 'hot_off_the_press',
};
const TOPIC_URLS: Record<string, string> = {
  '': '/',
  health: '/med',
  science: '/sci',
  psychology: '/psy',
  law: '/law',
  education: '/edu',
  hotp: '/hotp',
};


function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StoryComments({ storyId, token, setShowLogin }: { storyId: number; token: string | null; setShowLogin: (v: boolean) => void }) {
  const [comments, setComments] = useState<StoryComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');

  const fetchComments = useCallback(() => {
    fetch(`/api/story/${storyId}/comments`)
      .then(r => r.json())
      .then(setComments);
  }, [storyId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const postComment = async (parentId: number | null = null) => {
    if (!token) return setShowLogin(true);
    const content = parentId ? replyContent : newComment;
    if (!content.trim()) return;
    await fetch(`/api/story/${storyId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content, parent_id: parentId }),
    });
    if (parentId) {
      setReplyContent('');
      setReplyTo(null);
    } else {
      setNewComment('');
    }
    fetchComments();
  };

  const renderComment = (c: StoryComment, depth = 0) => (
    <div key={c.id} style={{ marginLeft: depth * 20, marginBottom: 8, paddingLeft: depth > 0 ? 12 : 0, borderLeft: depth > 0 ? '1px solid #cee3f8' : 'none' }}>
      <div style={{ fontSize: 12 }}>
        <b style={{ color: '#5f99cf' }}>{c.username}</b>
        <span style={{ color: '#888', marginLeft: 8, fontSize: 10 }}>{c.created_at ? timeAgo(c.created_at) : ''}</span>
      </div>
      <div style={{ fontSize: 13, margin: '4px 0' }}>{c.content}</div>
      {token && (
        <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyContent(''); }}
          style={{ fontSize: 10, background: 'none', border: 'none', color: '#5f99cf', cursor: 'pointer', padding: 0 }}>
          {replyTo === c.id ? 'cancel' : 'reply'}
        </button>
      )}
      {replyTo === c.id && (
        <div style={{ marginTop: 6 }}>
          <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)}
            placeholder="Write a reply..." style={{ width: '100%', height: 50, fontSize: 12, padding: 6, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4 }} />
          <button onClick={() => postComment(c.id)} style={{ fontSize: 11, padding: '3px 10px', marginTop: 4, background: '#5f99cf', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>reply</button>
        </div>
      )}
      {c.replies && c.replies.map((r: StoryComment) => renderComment(r, depth + 1))}
    </div>
  );

  return (
    <div className="story-comments">
      {token ? (
        <div style={{ marginBottom: 16 }}>
          <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
            placeholder="What are your thoughts?"
            style={{ width: '100%', height: 60, fontSize: 13, padding: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4 }} />
          <button onClick={() => postComment()} style={{ marginTop: 6, fontSize: 12, padding: '4px 14px', background: '#5f99cf', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>comment</button>
        </div>
      ) : (
        <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          <button onClick={() => setShowLogin(true)} style={{ background: 'none', border: 'none', color: '#5f99cf', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 12 }}>Log in</button> to join the discussion.
        </p>
      )}
      {comments.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No comments yet. Be the first to discuss this story.</p>}
      {comments.map(c => renderComment(c))}
    </div>
  );
}

export default function HomePage({ token, setShowLogin, defaultTopic }: { token?: string | null; setShowLogin?: (v: boolean) => void; defaultTopic?: string }) {
  const { t, apiLang } = useI18n();
  const api = (url: string) => localizeApiUrl(url, apiLang);
  const [stories, setStories] = useState<Story[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTopic, setActiveTopic] = useState(defaultTopic || '');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [totalPages, setTotalPages] = useState(1);
  const isHotp = activeTopic === 'hotp';

  // Sync activeTopic when route changes (defaultTopic prop changes)
  useEffect(() => {
    setActiveTopic(defaultTopic || '');
    setPage(1);
  }, [defaultTopic]);

  const loadStories = useCallback((p: number, topic: string) => {
    const topicParam = topic ? `&topic=${topic}` : '';
    fetch(api(`/api/health-stories?page=${p}&per_page=15${topicParam}`))
      .then(r => r.json())
      .then(data => {
        setStories(data.stories || []);
        setHasMore(data.has_more || false);
        setTotalPages(data.total_pages || 1);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  }, []);

  useEffect(() => {
    setPage(1);
    setLoading(true);
    loadStories(1, activeTopic);
  }, [activeTopic, loadStories]);

  const toggleComments = (storyId: number) => {
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(storyId)) next.delete(storyId);
      else next.add(storyId);
      return next;
    });
  };

  return (
    <>
      <div className="homepage-hero">
        <h1>{isHotp ? t.hot_off_the_press : t.story_home_title}</h1>
        <p>{isHotp ? t.hotp_subtitle : t.story_home_subtitle}</p>
        <div className="topic-tabs">
          {TOPIC_KEYS.map(key => (
            <Link
              key={key}
              to={TOPIC_URLS[key]}
              className={`topic-tab${activeTopic === key ? ' active' : ''}`}
            >
              {(t as any)[TOPIC_I18N[key]]}
            </Link>
          ))}
        </div>
      </div>

      <div className="homepage-feed">
        {loading && stories.length === 0 && (
          <div className="homepage-loading">Loading...</div>
        )}

        {!loading && stories.length === 0 && (
          <div className="homepage-empty">
            <h2>Coming Soon</h2>
            <p>Stories are generated daily. Check back soon, or <Link to="/jf">browse the journal feed</Link>.</p>
          </div>
        )}

        {stories.map(story => (
          <article key={story.id} className="story-card">
            <div className="story-source">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {story.source_url ? (
                  <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="story-source-name">{story.source_name}</a>
                ) : (
                  <span className="story-source-name">{story.source_name}</span>
                )}
                {story.topic === 'hotp' ? (
                  <>
                    <span className="story-topic-badge topic-hotp">HOT OFF THE PRESS</span>
                    {story.article_category && (
                      <span className={`story-topic-badge topic-${
                        story.article_category === 'Medicine' ? 'health' :
                        story.article_category === 'Science' ? 'science' :
                        story.article_category.toLowerCase()
                      }`}>{
                        story.article_category === 'Medicine' ? 'health' :
                        story.article_category.toLowerCase()
                      }</span>
                    )}
                  </>
                ) : story.topic ? (
                  <span className={`story-topic-badge topic-${story.topic}`}>{story.topic}</span>
                ) : null}
              </div>
              <span className="story-time">{timeAgo(story.created_at)}</span>
            </div>
            <h2 className="story-headline"><Link to={`/story/${story.slug || story.id}`}>{story.headline}</Link></h2>
            <p className="story-summary">{story.summary}</p>

            {/* MSM source links — hide for HOTP since they're not in the press yet */}
            {story.topic !== 'hotp' && story.msm_sources && story.msm_sources.length > 0 && (
              <div className="story-msm-sources">
                <span className="msm-label">{t.story_in_press}</span>
                {story.msm_sources.map((src, i) => (
                  <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="msm-link">
                    {src.name}
                  </a>
                ))}
              </div>
            )}

            <div className="story-footer">
              {story.paper_title && (
                <span className="story-paper-ref">
                  {t.story_referenced}{' '}
                  {story.article_slug ? (
                    <Link to={`/jf/article/${story.article_slug}`} className="story-paper-link">{story.paper_title}</Link>
                  ) : story.paper_doi ? (
                    <a href={`https://doi.org/${story.paper_doi}`} target="_blank" rel="noopener noreferrer" className="story-paper-link">{story.paper_title}</a>
                  ) : story.source_url ? (
                    <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="story-paper-link">{story.paper_title}</a>
                  ) : (
                    <span>{story.paper_title}</span>
                  )}
                  {story.article_publisher && (
                    <span className="story-journal-name"> — {story.article_publisher}</span>
                  )}
                </span>
              )}
              <button className="story-comments-toggle" onClick={() => toggleComments(story.id)}>
                {expandedComments.has(story.id) ? 'Hide' : ''} Comments ({story.comment_count})
              </button>
            </div>

            {expandedComments.has(story.id) && (
              <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 16 }}>
                <StoryComments
                  storyId={story.id}
                  token={token || null}
                  setShowLogin={setShowLogin || (() => {})}
                />
              </div>
            )}
          </article>
        ))}

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={page <= 1}
              onClick={() => { const p = page - 1; setPage(p); loadStories(p, activeTopic); }}
            >
              &larr; Newer
            </button>
            <span className="pagination-info">Page {page} of {totalPages}</span>
            <button
              className="pagination-btn"
              disabled={!hasMore}
              onClick={() => { const p = page + 1; setPage(p); loadStories(p, activeTopic); }}
            >
              Older &rarr;
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export function StoryPage({ token, setShowLogin }: { token?: string | null; setShowLogin?: (v: boolean) => void }) {
  const { slug } = useParams();
  const { t, apiLang } = useI18n();
  const api = (url: string) => localizeApiUrl(url, apiLang);
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try slug-based lookup first; fall back to ID for old links
    const isNumeric = /^\d+$/.test(slug || '');
    const url = isNumeric
      ? api(`/api/health-stories/${slug}`)
      : api(`/api/health-stories/by-slug/${slug}`);
    fetch(url)
      .then(r => r.json())
      .then(data => { setStory(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="homepage-loading" style={{ padding: 40 }}>Loading...</div>;
  if (!story) return <div className="homepage-empty" style={{ padding: 40 }}><h2>Story not found</h2></div>;

  return (
    <div className="story-page">
      <div className="story-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {story.source_url ? (
            <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="story-source-name">{story.source_name}</a>
          ) : (
            <span className="story-source-name">{story.source_name}</span>
          )}
          {story.topic && story.topic !== 'health' && (
            <span className={`story-topic-badge topic-${story.topic}`}>{story.topic}</span>
          )}
          <span className="story-time">{timeAgo(story.created_at)}</span>
        </div>
        <h1 className="story-page-title">{story.headline}</h1>
      </div>

      <div className="story-page-body">
        <p>{story.summary}</p>
      </div>

      {story.msm_sources && story.msm_sources.length > 0 && (
        <div className="story-msm-sources">
          <span className="msm-label">{t.story_in_press}</span>
          {story.msm_sources.map((src, i) => (
            <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="msm-link">
              {src.name}
            </a>
          ))}
        </div>
      )}

      <div className="story-footer" style={{ marginTop: 16 }}>
        {story.paper_title && (
          <span className="story-paper-ref">
            {t.story_referenced}{' '}
            {story.article_slug ? (
              <Link to={`/jf/article/${story.article_slug}`} className="story-paper-link">{story.paper_title}</Link>
            ) : story.paper_doi ? (
              <a href={`https://doi.org/${story.paper_doi}`} target="_blank" rel="noopener noreferrer" className="story-paper-link">{story.paper_title}</a>
            ) : story.source_url ? (
              <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="story-paper-link">{story.paper_title}</a>
            ) : (
              <span>{story.paper_title}</span>
            )}
          </span>
        )}
        {story.source_url && (
          <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="story-original-link">
            {t.story_read_original}
          </a>
        )}
      </div>

      <div style={{ borderTop: '1px solid #eee', marginTop: 24, paddingTop: 24 }}>
        <h3 style={{ fontSize: 16, margin: '0 0 16px', color: '#333' }}>Comments</h3>
        <StoryComments
          storyId={story.id}
          token={token || null}
          setShowLogin={setShowLogin || (() => {})}
        />
      </div>

      <div style={{ marginTop: 24 }}>
        <Link to="/" style={{ color: '#5f99cf', fontSize: 13 }}>&larr; Back to all stories</Link>
      </div>
    </div>
  );
}
